import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Percent, Calculator, History, FileDown, Mail } from "lucide-react";

export default function Commissions() {
  // ITER197 (Feb 2026): permitir seleccionar pestaña vía ?tab= (para deep-link desde otros menús)
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const initialTab = ["config", "com", "aj"].includes(params?.get("tab")) ? params.get("tab") : "config";
  return (
    <div className="space-y-6" data-testid="commissions-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">RRHH · Contabilidad</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Comisiones de vendedores</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Configure porcentajes por vendedor. Las comisiones se calculan automáticamente al autorizar facturas y se ajustan al procesarse devoluciones.</p>
      </div>
      <Tabs defaultValue={initialTab}>
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="config"><Percent size={14} className="mr-2" />Configuración</TabsTrigger>
          <TabsTrigger value="com"><Calculator size={14} className="mr-2" />Comisiones generadas</TabsTrigger>
          <TabsTrigger value="aj"><History size={14} className="mr-2" />Descuentos por devoluciones</TabsTrigger>
        </TabsList>
        <TabsContent value="config"><Config /></TabsContent>
        <TabsContent value="com"><Generadas /></TabsContent>
        <TabsContent value="aj"><Ajustes /></TabsContent>
      </Tabs>
    </div>
  );
}

function Config() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ vendedor_id: "", porcentaje: 5, tipo_calculo: "sobre_venta" });
  const load = async () => {
    const [c, u] = await Promise.all([api.get("/commissions/configuracion"), api.get("/users/basic").catch(() => ({ data: [] }))]);
    setItems(c.data); setUsers(u.data || []);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);
  const save = async () => {
    if (!form.vendedor_id) return toast.error("Seleccione vendedor");
    try { await api.post("/commissions/configuracion", form); toast.success("Configuración guardada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const recalcular = () => api.post("/commissions/recalcular").then(r => { toast.success(`${r.data.creados} comisiones creadas para facturas autorizadas`); }).catch(e => toast.error(formatApiError(e)));
  const userById = useMemo(() => {
    const m = {};
    for (const u of users) m[u.id] = u;
    return m;
  }, [users]);
  const nombreVendedor = (vid) => {
    const u = userById[vid];
    if (!u) return vid?.slice(0, 8) + "..." || "—";
    return `${u.nombres || ""} ${u.apellidos || ""}`.trim() || u.email;
  };
  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-md border border-[#d1dbd6] bg-white p-4 grid md:grid-cols-4 gap-3 items-end">
        <div className="md:col-span-2">
          <Label>Vendedor</Label>
          <Select value={form.vendedor_id || undefined} onValueChange={(v) => setForm({ ...form, vendedor_id: v })}>
            <SelectTrigger data-testid="commissions-vendedor-select"><SelectValue placeholder="Seleccione vendedor…" /></SelectTrigger>
            <SelectContent>
              {users
                .filter(u => u.activo !== false && ["ventas","vendedor","admin","gerencia"].includes(u.role))
                .map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {`${u.nombres || ""} ${u.apellidos || ""}`.trim() || u.email}
                    <span className="ml-1 text-[10px] text-[#5c7368]">· {u.role}</span>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Porcentaje</Label><Input type="number" step="0.1" value={form.porcentaje} onChange={(e) => setForm({ ...form, porcentaje: parseFloat(e.target.value) || 0 })} /></div>
        <div><Label>Base</Label><Select value={form.tipo_calculo} onValueChange={(v) => setForm({ ...form, tipo_calculo: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="sobre_venta">Sobre venta</SelectItem><SelectItem value="sobre_margen">Sobre margen</SelectItem></SelectContent></Select></div>
        <div className="md:col-span-4 flex gap-2">
          <Button onClick={save} className="bg-[#205c45]" data-testid="save-config-btn">Guardar / Actualizar</Button>
          <Button variant="outline" onClick={recalcular} data-testid="recalc-btn">Recalcular comisiones históricas</Button>
        </div>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Vendedor</th><th className="text-right">%</th><th>Base</th><th>Activo</th></tr></thead>
          <tbody>
            {items.map(c => (
              <tr key={c.id}>
                <td>
                  <div className="font-medium">{nombreVendedor(c.vendedor_id)}</div>
                  <div className="font-mono text-[10px] text-[#8ba397]">{c.vendedor_id}</div>
                </td>
                <td className="text-right tabular-nums font-semibold">{c.porcentaje}%</td>
                <td className="capitalize">{c.tipo_calculo?.replace("_", " ")}</td>
                <td>{c.activo ? "Sí" : "No"}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-[#8ba397]">Sin configuraciones</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Generadas() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/commissions/comisiones").then(r => setItems(r.data)).catch(e => toast.error(formatApiError(e))); }, []);
  const total = useMemo(() => items.reduce((s, x) => s + (x.valor_ajustado || 0), 0), [items]);
  const pagar = (c) => api.post(`/commissions/comisiones/${c.id}/pagar`).then(() => { toast.success("Marcada como pagada"); api.get("/commissions/comisiones").then(r => setItems(r.data)); }).catch(e => toast.error(formatApiError(e)));
  return (
    <div className="mt-4 space-y-4">
      <div className="text-xs text-[#5c7368]">Total pendiente/pagado: <strong className="text-[#15241d] tabular-nums">${total.toFixed(2)}</strong></div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Factura</th><th>Fecha</th><th>Vendedor</th><th className="text-right">Base</th><th className="text-right">%</th><th className="text-right">Calculada</th><th className="text-right">Ajustada</th><th>Estado</th><th className="w-24"></th></tr></thead>
          <tbody>
            {items.map(c => (
              <tr key={c.id}>
                <td className="font-mono text-xs">{c.factura_numero}</td>
                <td>{c.fecha}</td>
                <td className="font-mono text-xs">{c.vendedor_id?.slice(0, 8)}...</td>
                <td className="text-right tabular-nums">${(c.base_calculo||0).toFixed(2)}</td>
                <td className="text-right tabular-nums">{c.porcentaje}%</td>
                <td className="text-right tabular-nums">${(c.valor_comision||0).toFixed(2)}</td>
                <td className="text-right tabular-nums font-semibold">${(c.valor_ajustado||0).toFixed(2)}</td>
                <td><Badge className="capitalize">{c.estado}</Badge></td>
                <td>{c.estado === "pendiente" && <Button size="sm" variant="outline" onClick={() => pagar(c)}>Pagar</Button>}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-[#8ba397]">Sin comisiones aún</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Ajustes() {
  const [items, setItems] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [users, setUsers] = useState([]);
  const [filtros, setFiltros] = useState({ vendedor_id: "", desde: "", hasta: "" });
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [diag, setDiag] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filtros.vendedor_id) qs.append("vendedor_id", filtros.vendedor_id);
      if (filtros.desde) qs.append("desde", filtros.desde);
      if (filtros.hasta) qs.append("hasta", filtros.hasta);
      const [{ data: rows }, { data: agg }, { data: u }] = await Promise.all([
        api.get(`/commissions/ajustes?${qs.toString()}`),
        api.get(`/commissions/ajustes/resumen?${qs.toString()}`),
        api.get("/users/basic").catch(() => ({ data: [] })),
      ]);
      setItems(rows || []);
      setResumen(agg || []);
      setUsers(u || []);
      // ITER200: si el resultado es vacío y hay algún filtro, consultar diagnóstico
      if ((rows || []).length === 0 && (filtros.vendedor_id || filtros.desde || filtros.hasta)) {
        const dqs = filtros.vendedor_id ? `?vendedor_id=${encodeURIComponent(filtros.vendedor_id)}` : "";
        api.get(`/commissions/ajustes/diagnostico${dqs}`).then(r => setDiag(r.data)).catch(() => setDiag(null));
      } else {
        setDiag(null);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filtros.vendedor_id, filtros.desde, filtros.hasta]);
  useEffect(() => { load(); }, [load]);

  const nombreVendedor = (vid) => {
    const u = users.find(x => x.id === vid);
    if (!u) return vid?.slice(0, 8) + "…" || "—";
    return `${u.nombres || ""} ${u.apellidos || ""}`.trim() || u.email;
  };

  const totalGeneral = items.reduce((s, a) => s + Math.abs(a.monto || 0), 0);

  const quitarAjuste = async (a) => {
    if (!window.confirm(
      `¿Quitar el descuento de la NC ${a.nc_numero || ""} por $${Math.abs(a.monto || 0).toFixed(2)}?\n\n` +
      `El valor se devolverá a la comisión del vendedor.`
    )) return;
    try {
      const { data } = await api.delete(`/commissions/ajustes/${a.id}`);
      toast.success(`Descuento quitado · $${(data.monto_devuelto || 0).toFixed(2)} devuelto a la comisión`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="ajustes-tab">
      {/* Filtros */}
      <div className="rounded-md border border-[#d1dbd6] bg-white p-3 grid md:grid-cols-4 gap-3 items-end">
        <div className="md:col-span-2">
          <Label>Vendedor</Label>
          <Select value={filtros.vendedor_id || "__all__"} onValueChange={(v) => setFiltros(f => ({ ...f, vendedor_id: v === "__all__" ? "" : v }))}>
            <SelectTrigger data-testid="ajustes-vendedor-filter"><SelectValue placeholder="Todos los vendedores" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los vendedores</SelectItem>
              {users.filter(u => u.activo !== false && ["ventas","vendedor","admin","gerencia"].includes(u.role)).map(u => (
                <SelectItem key={u.id} value={u.id}>{`${u.nombres || ""} ${u.apellidos || ""}`.trim() || u.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Desde</Label><Input type="date" value={filtros.desde} onChange={(e) => setFiltros(f => ({ ...f, desde: e.target.value }))} data-testid="ajustes-desde" /></div>
        <div><Label>Hasta</Label><Input type="date" value={filtros.hasta} onChange={(e) => setFiltros(f => ({ ...f, hasta: e.target.value }))} data-testid="ajustes-hasta" /></div>
      </div>

      {/* ITER199 (Feb 2026): botón limpiar filtros cuando hay alguno activo */}
      {(filtros.vendedor_id || filtros.desde || filtros.hasta) && (
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={() => setFiltros({ vendedor_id: "", desde: "", hasta: "" })}
            className="text-[#205c45] hover:underline font-medium"
            data-testid="ajustes-clear-filters"
          >
            ✕ Limpiar filtros
          </button>
          <span className="text-[#8ba397]">·</span>
          <span className="text-[#5c7368]">
            {filtros.vendedor_id && "Vendedor: filtrado"}
            {(filtros.desde || filtros.hasta) && ` · Rango: ${filtros.desde || "…"} → ${filtros.hasta || "…"}`}
          </span>
        </div>
      )}

      {/* ITER198: Acciones de liquidación por vendedor */}
      {filtros.vendedor_id && (
        <LiquidacionActions
          vendedorId={filtros.vendedor_id}
          vendedorNombre={nombreVendedor(filtros.vendedor_id)}
          desde={filtros.desde}
          hasta={filtros.hasta}
        />
      )}
      {!filtros.vendedor_id && (
        <div className="rounded-md border border-dashed border-[#d1dbd6] bg-[#f5f7f6] px-4 py-3 text-xs text-[#5c7368]">
          <span className="font-semibold">💡 Tip:</span> selecciona un vendedor arriba para poder descargar su liquidación en PDF o enviarla por email a Ventas.
        </div>
      )}

      {/* ITER205: gestión manual de NCs descontables */}
      <div className="rounded-md border border-dashed border-[#b2d0c2] bg-[#e9f2ee] px-3 py-2 flex flex-col md:flex-row md:items-center gap-2 text-xs">
        <span className="flex-1 text-[#4a5c53]">
          <strong>¿Falta o sobra un descuento?</strong> Puede añadir manualmente una nota de crédito a esta lista,
          o quitar cualquier descuento con el botón <em>Quitar</em> de la tabla (el valor se devuelve a la comisión).
        </span>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="px-3 py-1 rounded bg-[#205c45] text-white hover:bg-[#1a4a37] font-medium whitespace-nowrap"
          data-testid="ajustes-add-nc-btn"
        >
          + Añadir NC al descuento
        </button>
        <button
          type="button"
          onClick={async () => {
            if (!window.confirm("Crear los ajustes faltantes de las NCs marcadas como \"descontar al vendedor\".\n\nEs idempotente — no crea duplicados.")) return;
            try {
              const { data } = await api.post("/commissions/ajustes/backfill-desde-ncs");
              toast.success(
                `${data.ajustes_creados} ajuste(s) creado(s) · ${data.ncs_procesadas} NCs marcadas revisadas · ${data.ncs_ya_ajustadas} ya tenían ajuste`,
                { duration: 9000 }
              );
              load();
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          className="px-3 py-1 rounded border border-[#205c45] text-[#205c45] hover:bg-white font-medium whitespace-nowrap"
          data-testid="ajustes-backfill-ncs-btn"
        >
          Reparar marcadas
        </button>
      </div>

      {/* Resumen agregado */}
      {resumen.length > 0 && (
        <div className="rounded-md border border-[#d1dbd6] bg-[#fdecea] p-3" data-testid="ajustes-resumen">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#7a2514] mb-2">
            Total descontado en el período: <span className="tabular-nums">${totalGeneral.toFixed(2)}</span> · {items.length} ajuste(s)
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            {resumen.map(r => (
              <div key={r.vendedor_id} className="bg-white rounded p-2 border border-[#d99584] text-xs">
                <div className="font-semibold text-[#15241d]">{nombreVendedor(r.vendedor_id)}</div>
                <div className="text-[#7a2514] tabular-nums font-bold text-lg">-${Math.abs(r.total_descontado).toFixed(2)}</div>
                <div className="text-[#5c7368]">{r.n_ajustes} devolución(es) · {r.n_clientes_afectados} cliente(s)</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabla detallada */}
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}></th>
              <th>Fecha NC</th>
              <th>Vendedor</th>
              <th>N° NC</th>
              <th>Factura origen</th>
              <th>Cliente</th>
              <th className="text-right">Base</th>
              <th className="text-right">%</th>
              <th className="text-right">Descuento</th>
              <th>Motivo</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(a => (
              <React.Fragment key={a.id}>
                <tr className="hover:bg-[#fafafa] cursor-pointer" onClick={() => setExpanded(e => ({ ...e, [a.id]: !e[a.id] }))} data-testid={`ajuste-row-${a.nc_numero || a.id}`}>
                  <td className="text-[#5c7368]">{expanded[a.id] ? "▾" : "▸"}</td>
                  <td>{a.nc_fecha || a.fecha}</td>
                  <td className="text-xs">{nombreVendedor(a.vendedor_id)}</td>
                  <td className="font-mono text-xs">{a.nc_numero || "—"}</td>
                  <td className="font-mono text-xs">{a.factura_numero || a.factura_id?.slice(0, 8) + "…"}</td>
                  <td className="text-xs">{a.cliente_nombre || "—"}</td>
                  <td className="text-right tabular-nums">${(a.base_calculo || 0).toFixed(2)}</td>
                  <td className="text-right tabular-nums">{(a.porcentaje || 0).toFixed(2)}%</td>
                  <td className="text-right tabular-nums text-[#7a2514] font-semibold">-${Math.abs(a.monto || 0).toFixed(2)}</td>
                  <td className="text-xs max-w-[200px] truncate" title={a.motivo}>{a.motivo}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="outline" className="h-7 text-[11px] text-[#7a2514] border-[#d99584]"
                      onClick={() => quitarAjuste(a)} data-testid={`ajuste-quitar-${a.nc_numero || a.id}`}>
                      Quitar
                    </Button>
                  </td>
                </tr>
                {expanded[a.id] && (a.lineas || []).length > 0 && (
                  <tr className="bg-[#f7f9f8]">
                    <td colSpan={11} className="p-0">
                      <div className="p-3">
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-[#5c7368] mb-2">
                          Productos devueltos ({a.lineas.length})
                        </div>
                        <table className="w-full text-xs">
                          <thead className="bg-white">
                            <tr>
                              <th className="text-left p-1.5">Producto</th>
                              <th className="text-left p-1.5">Lote</th>
                              <th className="text-right p-1.5">Cantidad</th>
                              <th className="text-right p-1.5">Precio unit.</th>
                              <th className="text-right p-1.5">Subtotal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {a.lineas.map((l, i) => (
                              <tr key={i} className="border-t border-[#eee]" data-testid={`ajuste-linea-${a.id}-${i}`}>
                                <td className="p-1.5">{l.producto_nombre || "—"}</td>
                                <td className="p-1.5 font-mono">{l.numero_lote || "—"}</td>
                                <td className="p-1.5 text-right tabular-nums">{l.cantidad} <span className="text-[9px] text-[#5c7368]">{l.unidad_venta?.[0]}</span></td>
                                <td className="p-1.5 text-right tabular-nums">${(l.precio_unitario || 0).toFixed(4)}</td>
                                <td className="p-1.5 text-right tabular-nums font-semibold">${(l.subtotal || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {items.length === 0 && !loading && (
              <tr><td colSpan={11} className="text-center py-8 text-[#8ba397]">
                Sin ajustes en el período seleccionado
                {diag && diag.total_en_bd > 0 && (
                  <div className="text-xs text-[#5c7368] mt-3 max-w-lg mx-auto text-left bg-[#f5f7f6] rounded p-3 border border-[#d1dbd6]">
                    <div className="font-semibold text-[#15241d] mb-1">📊 Diagnóstico</div>
                    <div>{filtros.vendedor_id ? `Este vendedor tiene ${diag.total_en_bd} ajuste(s) registrados en total` : `Hay ${diag.total_en_bd} ajuste(s) en la BD total`}</div>
                    <div>Rango disponible: <strong>{diag.min_fecha || "—"}</strong> → <strong>{diag.max_fecha || "—"}</strong></div>
                    {diag.ajustes_sin_tipo > 0 && (
                      <div className="text-[#a35400] mt-1">⚠ {diag.ajustes_sin_tipo} ajuste(s) legacy sin schema completo — aparecen si limpias filtros.</div>
                    )}
                    {diag.ultimos_5 && diag.ultimos_5.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-[#205c45]">Ver últimos 5 ajustes</summary>
                        <ul className="mt-1 space-y-1">
                          {diag.ultimos_5.map((u, i) => (
                            <li key={i} className="text-[11px]">
                              {u.fecha} · {u.numero_interno || "—"} · fact {u.factura_numero || "—"} · <span className="text-[#b53a25]">${Math.abs(u.monto || 0).toFixed(2)}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
                {(filtros.vendedor_id || filtros.desde || filtros.hasta) && (
                  <>
                    <br />
                    <button
                      type="button"
                      onClick={() => setFiltros({ vendedor_id: "", desde: "", hasta: "" })}
                      className="text-[#205c45] hover:underline text-xs mt-2"
                      data-testid="ajustes-empty-clear"
                    >
                      Quitar filtros y ver todos los ajustes
                    </button>
                  </>
                )}
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={11} className="text-center py-4 text-[#8ba397]">Cargando…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {addOpen && (
        <AgregarNcDialog
          vendedorId={filtros.vendedor_id}
          nombreVendedor={nombreVendedor}
          users={users}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// ITER212: añadir manualmente una NC a los descuentos por devoluciones,
// permitiendo asignar el vendedor cuando no se puede deducir de la factura.
function AgregarNcDialog({ vendedorId, nombreVendedor, users, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(null);
  const [asign, setAsign] = useState({}); // nc_id -> vendedor_id elegido

  const vendedores = (users || []).filter(u => u.activo !== false && ["ventas", "vendedor", "admin", "gerencia"].includes(u.role));

  useEffect(() => {
    const qs = vendedorId ? `?vendedor_id=${encodeURIComponent(vendedorId)}` : "";
    api.get(`/commissions/ncs-descontables${qs}`)
      .then(r => setRows(r.data || []))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [vendedorId]);

  const visibles = rows.filter(r => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [r.nc_numero, r.factura_numero, r.cliente_nombre, r.motivo].some(v => (v || "").toLowerCase().includes(s));
  });

  const vendedorDe = (r) => r.vendedor_id || asign[r.nc_id] || vendedorId || "";
  const listosCount = visibles.filter(r => vendedorDe(r)).length;

  const agregar = async (r) => {
    const vend = vendedorDe(r);
    if (!vend) {
      toast.error("Seleccione el vendedor al que se debe descontar esta NC");
      return false;
    }
    setSaving(r.nc_id);
    try {
      await api.post("/commissions/ajustes/desde-nc", {
        nc_id: r.nc_id,
        vendedor_id: r.vendedor_id ? null : vend,
      });
      toast.success(`NC ${r.nc_numero} añadida a los descuentos`);
      setRows(prev => prev.filter(x => x.nc_id !== r.nc_id));
      onSaved();
      return true;
    } catch (e) { toast.error(formatApiError(e)); return false; }
    finally { setSaving(null); }
  };

  const agregarTodas = async () => {
    const listos = visibles.filter(r => vendedorDe(r));
    if (listos.length === 0) return toast.error("Seleccione el vendedor de las NCs que desea descontar");
    if (!window.confirm(`¿Añadir ${listos.length} nota(s) de crédito al descuento por devoluciones?`)) return;
    setSaving("__all__");
    let ok = 0;
    for (const r of listos) {
      try {
        await api.post("/commissions/ajustes/desde-nc", {
          nc_id: r.nc_id, vendedor_id: r.vendedor_id ? null : vendedorDe(r),
        });
        ok += 1;
      } catch { /* continuar */ }
    }
    setSaving(null);
    setRows(prev => prev.filter(x => !listos.find(v => v.nc_id === x.nc_id)));
    toast.success(`${ok} de ${listos.length} NC añadidas al descuento`);
    onSaved();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Añadir nota de crédito al descuento por devoluciones</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[#5c7368]">
          Se listan todas las NCs que aún no tienen descuento registrado. Si el sistema no puede deducir el
          vendedor desde la factura, elíjalo en la columna <strong>Vendedor</strong> y pulse Añadir.
        </p>
        <div className="flex gap-2 items-center">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por NC, factura, cliente o motivo…" data-testid="add-nc-search" />
          <Button size="sm" variant="outline" disabled={listosCount === 0 || saving === "__all__"}
            onClick={agregarTodas} className="whitespace-nowrap" data-testid="add-nc-todas-btn"
            title="Añade solo las NCs que ya tienen vendedor asignado">
            {saving === "__all__" ? "Añadiendo…" : `Añadir listas (${listosCount})`}
          </Button>
        </div>
        <div className="border border-[#d1dbd6] rounded-md max-h-[26rem] overflow-auto">
          <table className="erp-table">
            <thead><tr>
              <th>Fecha</th><th>N° NC</th><th>Factura</th><th>Cliente</th><th className="min-w-[190px]">Vendedor</th>
              <th className="text-right">Subtotal</th><th className="text-right">Descuento est.</th><th className="w-24"></th>
            </tr></thead>
            <tbody>
              {visibles.map(r => {
                const vend = vendedorDe(r);
                const porc = r.porcentaje;
                const estimado = porc ? (r.subtotal || 0) * porc / 100 : null;
                return (
                  <tr key={r.nc_id} data-testid={`add-nc-row-${r.nc_numero}`}>
                    <td className="text-xs">{r.nc_fecha}</td>
                    <td className="font-mono text-xs">{r.nc_numero}</td>
                    <td className="font-mono text-xs">{r.factura_numero || "—"}</td>
                    <td className="text-xs">{r.cliente_nombre || "—"}</td>
                    <td className="text-xs">
                      {r.vendedor_id ? (
                        <>
                          {nombreVendedor(r.vendedor_id)}
                          {r.sin_comision_registrada && <div className="text-[10px] text-[#8a5500]">% de su configuración</div>}
                        </>
                      ) : (
                        <>
                          <Select value={vend || "__none__"} onValueChange={(v) => setAsign(a => ({ ...a, [r.nc_id]: v === "__none__" ? "" : v }))}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`add-nc-vendedor-${r.nc_numero}`}>
                              <SelectValue placeholder="Elegir vendedor" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">Elegir vendedor…</SelectItem>
                              {vendedores.map(u => (
                                <SelectItem key={u.id} value={u.id}>{`${u.nombres || ""} ${u.apellidos || ""}`.trim() || u.email}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {r.motivo_no_descontable && (
                            <div className="text-[10px] text-[#8a5500] mt-0.5">{r.motivo_no_descontable}</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="text-right tabular-nums text-xs">${(r.subtotal || 0).toFixed(2)}</td>
                    <td className="text-right tabular-nums text-xs font-semibold text-[#7a2514]">
                      {estimado !== null ? `-$${estimado.toFixed(2)}` : <span className="text-[#8ba397] font-normal">según %</span>}
                    </td>
                    <td>
                      <Button size="sm" className="h-7 text-[11px] bg-[#205c45]" disabled={saving === r.nc_id || !vend}
                        onClick={() => agregar(r)} data-testid={`add-nc-btn-${r.nc_numero}`}>
                        {saving === r.nc_id ? "…" : "Añadir"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {visibles.length === 0 && !loading && (
                <tr><td colSpan={8} className="text-center py-8 text-xs text-[#8ba397]">
                  No hay notas de crédito pendientes de descontar
                </td></tr>
              )}
              {loading && <tr><td colSpan={8} className="text-center py-6 text-xs text-[#8ba397]">Cargando…</td></tr>}
            </tbody>
          </table>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ITER198 (Feb 2026): Acciones de liquidación por vendedor (PDF + email manual).
// Envío MANUAL — Contabilidad revisa antes. Destino: ventas@pharma-stern.com.
function LiquidacionActions({ vendedorId, vendedorNombre, desde, hasta }) {
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [openDlg, setOpenDlg] = useState(false);
  const [emailDestino, setEmailDestino] = useState("ventas@pharma-stern.com");
  const [observaciones, setObservaciones] = useState("");

  const descargarPDF = async () => {
    setDownloading(true);
    try {
      const qs = new URLSearchParams({ vendedor_id: vendedorId });
      if (desde) qs.append("desde", desde);
      if (hasta) qs.append("hasta", hasta);
      const resp = await api.get(`/commissions/liquidacion/pdf?${qs.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `liquidacion_${vendedorNombre.replace(/\s+/g, "_")}_${desde || "inicio"}_${hasta || "hoy"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDownloading(false); }
  };

  const enviarEmail = async () => {
    if (!window.confirm(`Confirmar envío de la liquidación de ${vendedorNombre} a ${emailDestino}?\n\nEste envío es manual — Contabilidad ya validó los números.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/commissions/liquidacion/enviar-email", {
        vendedor_id: vendedorId,
        desde: desde || null,
        hasta: hasta || null,
        email_destino: emailDestino,
        observaciones: observaciones || null,
      });
      toast.success(`Enviado a ${data.email_destino} · neto: $${data.neto?.toFixed(2)}`, { duration: 6000 });
      setOpenDlg(false);
      setObservaciones("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="rounded-md border border-[#b2d0c2] bg-[#f0f7f4] p-3 flex items-center gap-3 flex-wrap" data-testid="liquidacion-actions">
      <div className="flex-1 text-sm">
        <span className="font-semibold text-[#15241d]">Liquidación de {vendedorNombre}</span>
        <span className="text-[#5c7368]"> · periodo {desde || "inicio"} → {hasta || "hoy"}</span>
      </div>
      <Button
        variant="outline"
        onClick={descargarPDF}
        disabled={downloading}
        data-testid="liq-download-btn"
        className="border-[#205c45] text-[#205c45]"
      >
        <FileDown size={14} className="mr-1" />{downloading ? "Generando…" : "Descargar PDF"}
      </Button>
      <Button
        onClick={() => setOpenDlg(o => !o)}
        className="bg-[#205c45] hover:bg-[#1a4a37]"
        data-testid="liq-email-btn"
      >
        <Mail size={14} className="mr-1" />Enviar por email
      </Button>
      {openDlg && (
        <div className="w-full rounded border border-[#d1dbd6] bg-white p-3 space-y-2 mt-2">
          <div className="text-xs font-semibold text-[#5c7368] uppercase tracking-wider">Envío manual · revisión de Contabilidad</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Email destino</Label>
              <Input value={emailDestino} onChange={(e) => setEmailDestino(e.target.value)} data-testid="liq-email-destino" />
            </div>
            <div>
              <Label className="text-xs">Observaciones (opcional)</Label>
              <Input placeholder="Ej: comisiones aprobadas, cerrar el mes" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} data-testid="liq-email-obs" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setOpenDlg(false)}>Cancelar</Button>
            <Button size="sm" onClick={enviarEmail} disabled={sending || !emailDestino} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="liq-email-confirm">
              {sending ? "Enviando…" : "Confirmar envío"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

